import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMediaAvailability, MediaAvailability } from '@/api/media';
import { isSwLive } from '@/sw/mediaSw';
import { MediaTask, getMediaTask, subscribeMediaTask } from '@/utils/mediaFetchQueue';

export const MEDIA_AVAILABILITY_KEY = ['media-availability'];

/**
 * Whether a manuscript scraper account can reach one message's file.
 *
 * `enabled` is false until a reviewer actually asks: a chat page renders ~50
 * bubbles and this is a per-message route, so fetching on render would be 50
 * requests per scroll for an answer almost nobody looks at.
 *
 * The answer is derived from `scraper_account_chats`, which only a join or a
 * `sync_dialogs` run moves — minutes-stale is fine, hence the long staleTime.
 * `retry: false` because the expected failure here is a 404 (the message is
 * outside the manuscript channel set), and retrying a scope clamp three times
 * just delays the message the card wants to show.
 */
export const useMediaAvailability = (messageId: number | null | undefined, enabled = true) =>
  useQuery<MediaAvailability>({
    queryKey: [...MEDIA_AVAILABILITY_KEY, messageId ?? null],
    queryFn: () => getMediaAvailability(messageId as number),
    enabled: enabled && !!messageId,
    staleTime: 5 * 60_000,
    retry: false,
  });

/**
 * One file's delivery state, from the module-scope queue in
 * `utils/mediaFetchQueue.ts`.
 *
 * `useSyncExternalStore` rather than react-query on purpose: the queue is a
 * per-USER limiter with its own poll loop, and a download must survive the
 * component unmounting when a reviewer scrolls the bubble out of view. A
 * query cache keyed per component would give each bubble its own budget and
 * kill the transfer on unmount — the two things §11.7 exists to prevent.
 */
export const useMediaTask = (messageId: number): MediaTask =>
  useSyncExternalStore(
    useCallback((listener) => subscribeMediaTask(messageId, listener), [messageId]),
    useCallback(() => getMediaTask(messageId), [messageId])
  );

/**
 * Whether the service-worker delivery path is usable right now (§6.4): the
 * worker is ready, this page is CONTROLLED by it, and it answered a live probe.
 *
 * `null` while the answer is unknown — the caller renders neither path until it
 * resolves, because guessing wrong means either a broken image (guessing the
 * worker is alive when it is not) or a needless in-memory copy of a 100 MB PDF
 * (guessing the other way).
 */
export const useSwLive = (): boolean | null => {
  const [live, setLive] = useState<boolean | null>(null);
  useEffect(() => {
    let canceled = false;
    void isSwLive().then((result) => { if (!canceled) setLive(result); });
    return () => { canceled = true; };
  }, []);
  return live;
};
